import React from "react";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import GHGTargetSetForm from "components/GHGTargetSetForm";
import type { GHGTargetForm, GHGTargetGridRow } from "components/GHGTargetSetForm";
import {
  ClientGHGTargetModel,
  ClientScope3GHGReduction,
} from "pages/Client/data/ClientGHGTargetModel";
import {
  _addClientGHGTarget,
  _deleteClientGHGTarget,
  _getClientGHGTargetByTargetNumber,
  _getCustomClientGHGTargetsByClientId,
  _updateClientGHGTarget,
} from "middlewares/ClientGHGTargetApi/clientGHGTarget";

// ── adapters ──────────────────────────────────────────────────────────────────

const toBaseForm = (t: ClientGHGTargetModel): GHGTargetForm => ({
  order: t.order,
  targetNumber: t.targetNumber,
  targetName: t.targetName,
  baseYear: t.baseYear,
  targetYear: t.targetYear,
  scopesCovered: t.scopesCovered,
  baselineValue: t.baselineValue,
  targetedValue: t.targetedValue,
  reduction: t.reduction,
  scope1Reduction: t.scope1Reduction,
  scope2Reduction: t.scope2Reduction,
  scope3GHGReduction:
    t.clientScope3GHGReduction?.map((s) => ({
      id: s.id,
      targetNumber: s.clientGHGTargetTargetNumber,
      catagory: s.catagory,
      catagoryName: s.catagoryName,
      reduction: s.reduction,
      createdBy: s.createdBy,
      modifiedBy: s.modifiedBy,
      createdAt: s.createdAt,
      modifiedAt: s.modifiedAt,
      isActive: s.isActive,
    })) ?? null,
  createdBy: t.createdBy,
  modifiedBy: t.modifiedBy,
  createdAt: t.createdAt,
  modifiedAt: t.modifiedAt,
  isActive: t.isActive,
});

const toClientModel = (f: GHGTargetForm, entityId: number): ClientGHGTargetModel => ({
  order: f.order,
  targetNumber: f.targetNumber,
  clientID: entityId || null,
  targetName: f.targetName,
  baseYear: f.baseYear,
  targetYear: f.targetYear,
  scopesCovered: f.scopesCovered,
  baselineValue: f.baselineValue,
  targetedValue: f.targetedValue,
  reduction: f.reduction,
  scope1Reduction: f.scope1Reduction,
  scope2Reduction: f.scope2Reduction,
  clientScope3GHGReduction:
    f.scope3GHGReduction?.map(
      (s): ClientScope3GHGReduction => ({
        id: s.id,
        clientGHGTargetTargetNumber: s.targetNumber,
        catagory: s.catagory,
        catagoryName: s.catagoryName,
        reduction: s.reduction,
        createdBy: s.createdBy,
        modifiedBy: s.modifiedBy,
        createdAt: s.createdAt,
        modifiedAt: s.modifiedAt,
        isActive: s.isActive,
      })
    ) ?? null,
  createdBy: f.createdBy,
  modifiedBy: f.modifiedBy,
  createdAt: f.createdAt,
  modifiedAt: f.modifiedAt,
  isActive: f.isActive,
});

// ── props ─────────────────────────────────────────────────────────────────────

interface ClientSetTargetProps {
  clientId: number;
  handleCloseSnackBar: (state: AlertsProps) => void;
}

// ── component ─────────────────────────────────────────────────────────────────

const ClientSetTarget: React.FC<ClientSetTargetProps> = ({ clientId, handleCloseSnackBar }) => (
  <GHGTargetSetForm
    entityId={clientId}
    handleCloseSnackBar={handleCloseSnackBar}
    testIds={{
      addBtn: "btnAddTarget",
      saveBtn: "btnSaveTarget",
      targetName: "targetName",
      scope1: "scope1Reduction",
      scope2: "scope2Reduction",
      allCategory: "allCategory",
      reductionPct: "reductionPercentage",
    }}
    api={{
      fetchGrid: async (id) => {
        const res = await _getCustomClientGHGTargetsByClientId(id);
        return (res ?? []) as GHGTargetGridRow[];
      },
      getTarget: async (tn) => {
        const res = await _getClientGHGTargetByTargetNumber(tn);
        if (!res) return null;
        return toBaseForm(res);
      },
      addTarget: (form, id) => _addClientGHGTarget(toClientModel(form, id)),
      updateTarget: (form, id) => _updateClientGHGTarget(toClientModel(form, id)),
      deleteTarget: _deleteClientGHGTarget,
    }}
  />
);

export default ClientSetTarget;
